import {
  Box,
  CustomPagination,
  MUIDataTable,
  MUIDataTableColumnDef,
  Typography,
} from 'web-library';
import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import {
  UserAccountsObject,
  UserGroupDetail,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';

const useStyles = makeStyles()((theme) =>
({
  nricText: {
    color: theme.color.text.textTertiary,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const TableFooter = ({
  classes,
  userAccounts,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
}: {
  classes: Record<string,string>;
  userAccounts: UserAccountsObject[];
  pageNumber: number;
  pageSize: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
}) => (
  <Box className={classes.paginationContainer}>
    <CustomPagination
      dataLength={userAccounts?.length || 0}
      page={pageNumber}
      rowsPerPage={pageSize}
      rowsOptions={[5, 10, 20, 40, 50]}
      setPage={(currentPage: number) => setPageNumber(currentPage)}
      setRowsPerPage={(numberOfRows: number) => {
        setPageNumber(0);
        setPageSize(numberOfRows);
      }}
    />
  </Box>
);

const tableColumns = (
  data: UserAccountsObject[],
  t: TFunction<'translation'>,
  classes: Record<string,string>
): MUIDataTableColumnDef[] => [
    {
      name: 'adminName',
      label: t('admin->>UserGroups.AdminName'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '50%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{data[dataIndex].name}</Typography>
            <Typography className={classes.nricText} variant="subtitle2">
              {data[dataIndex].username}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'email',
      label: t('admin->>UserGroups.Email'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '50%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].email}</Typography>
        ),
      },
    },
  ];

interface ViewAdminAccountDataTableProps {
  userGroupDetails: UserGroupDetail;
}

const ViewAdminAccountDataTable = ({ userGroupDetails }: ViewAdminAccountDataTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  return (
    <MUIDataTable
      columns={tableColumns(userGroupDetails.userAccounts, t, classes)}
      data={userGroupDetails.userAccounts}
      emptyDataLabel={t('announcementsNotifications->>SendNotification.NoRecipientMessage')}
      options={{
        pagination: true,
        sort: true,
        selectToolbarPlacement: 'none',
        selectableRows: 'none',
        serverSide: false,
        page: pageNumber,
        rowsPerPage: pageSize,
        onChangePage: (currentPage: number) => setPageNumber(currentPage),
        onChangeRowsPerPage: (numberOfRows: number) => setPageSize(numberOfRows),
      }}
      components={{
        TableFooter: () =>
          TableFooter({
            classes,
            userAccounts: userGroupDetails?.userAccounts,
            pageNumber,
            pageSize,
            setPageNumber,
            setPageSize,
          }),
      }}
      rowsPerPageOptions={[5, 10, 20, 40, 50]}
    />
  );
};

export default ViewAdminAccountDataTable;
